import { useState } from 'react';
import { api } from '../lib/api';
import { markInviteSent } from '../lib/inviteSent';
import './InviteEmail.css';

interface InviteEmailProps {
  /** The pair code to mail — whoever holds it holds the invite already. */
  code: string | null;
  /** /api/auth/config -> mailEnabled(). False hides the form entirely, so a
   *  click can never land on a 503 from a worker with no transport. */
  mailEnabled: boolean;
}

/**
 * "Email the invite" (card AnqHIFLf6a5y, re-pointed by card tJdisOEkVwha) —
 * the third way an invite leaves the app, beside Copy and Share. Shown
 * wherever the pair code is: the Pair screen's invite block and the share bar
 * at the top of the empty chat, which is where the creator actually lands
 * right after creating a couple.
 *
 * It sends the LINK, not the code. The link is unguessable and single-use, the
 * code is 6 characters and lives as long as the link (24 hours everywhere,
 * card HpgPwz4VbBRa decision (a)), so the mail hands over the safer credential
 * and the code stays on screen as the fallback for a partner already on a call
 * with the creator.
 *
 * The worker answers the same for a link that reaches nobody as for one that
 * does, so no line here ever confirms whether a couple exists — a failure
 * just points back at Copy.
 */
export function InviteEmail({ code, mailEnabled }: InviteEmailProps) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!mailEnabled || !code) return null;

  const send = async () => {
    const addr = email.trim();
    setMsg(null);
    setErr(null);
    if (!addr) {
      setErr('Enter Your Partner’s Email Address.');
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      await api('/api/pair/invite-link', { body: { email: addr } });
      setMsg(`Sent to ${addr}. The invitation holds for 24 hours and admits one join.`);
      setEmail('');
      // The link is on its way — this device has invited (card E5sHUI8IkN6b).
      markInviteSent();
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      setErr(
        raw.includes('rate_limited')
          ? 'Too many sends in a row — wait a minute, or copy the invite link instead.'
          : raw.includes('mail_unavailable')
            ? 'Email sending is not set up here — copy the invite link instead.'
            : raw.includes('already_paired')
              ? 'You are already paired — there is nobody left to invite.'
              : 'That Email Could Not Be Sent — Copy the Invite Link Instead.',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pear-invite-email" id="invite-email">
      <label className="pear-invite-email-label" htmlFor="input-invite-email">
        Or I can send your partner a 24-hour invitation
      </label>
      <div className="pear-invite-email-row">
        <input
          type="email"
          id="input-invite-email"
          placeholder="partner@example.com"
          className="pear-chat-input pear-input-center"
          aria-label="Partner email for the pair code"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void send();
          }}
        />
        <button
          type="button"
          className="pear-invite-btn"
          id="btn-invite-email"
          disabled={sending}
          onClick={() => void send()}
        >
          {sending ? 'Sending…' : 'Email the invite'}
        </button>
      </div>
      {msg && (
        <p className="pear-invite-email-status" id="invite-email-status" role="status">
          {msg}
        </p>
      )}
      {err && (
        <p className="pear-invite-email-error" id="invite-email-error" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}
